import React, { useState, useEffect } from 'react';

function ProductDetailsModal({ product, onClose, onAddToCart, isChefSpecialty = false, onStartChat }) {
  const [quantity, setQuantity] = useState(1);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedSides, setSelectedSides] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Reset when a new product is selected
    setCurrentImageIndex(0);
    setQuantity(1);
    setSelectedExtras([]);
    setSelectedSides([]);
  }, [product]);

  if (!product) return null;

  const imageSources = product.images || (product.image ? [product.image] : []);
  const imageSrc = imageSources[currentImageIndex];

  const handleIncrease = () => setQuantity(q => q + 1);
  const handleDecrease = () => setQuantity(q => (q > 1 ? q - 1 : 1));

  const handleExtraToggle = (extra) => {
    setSelectedExtras(prev => {
      if (prev.find(e => e.id === extra.id)) {
        return prev.filter(e => e.id !== extra.id);
      }
      return [...prev, extra];
    });
  };

  const handleSideToggle = (side) => {
    setSelectedSides(prev => {
      if (prev.find(s => s.id === side.id)) {
        return prev.filter(s => s.id !== side.id);
      }
      return [...prev, side];
    });
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % imageSources.length);
  };
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + imageSources.length) % imageSources.length);
  };

  const numericPrice = product.price ? parseFloat(product.price.toString().replace(/[^0-9.]/g, '')) : 0;
  const extrasTotal = selectedExtras.reduce((sum, extra) => sum + (parseFloat(extra.price) || 0), 0);
  const sidesTotal = selectedSides.reduce((sum, side) => sum + (parseFloat(side.price) || 0), 0);
  const totalNumericPrice = numericPrice + extrasTotal + sidesTotal;
  const displayPrice = totalNumericPrice ? product.price.toString().replace(/[\d.,]+/, (totalNumericPrice * quantity).toLocaleString()) : product.price;

  return (
    <div className="vendors-modal-overlay">
      <div className={`vendors-modal product-modal ${isChefSpecialty ? 'chef-specialty-modal' : ''}`}>
        <button className="vendors-close-btn product-close-btn" onClick={onClose}>×</button>
        
        <div className="product-modal-layout">
          
          {/* Right Side: Product Details & Cart */}
          <div className="product-modal-details">
            {isChefSpecialty ? (
              <>
                {imageSources.length > 0 && (
                  <div className="product-image-gallery">
                    <img src={imageSrc} alt={product.name} className="gallery-main-image" />
                    {imageSources.length > 1 && (
                      <>
                        <button onClick={handlePrevImage} className="gallery-nav prev">‹</button>
                        <button onClick={handleNextImage} className="gallery-nav next">›</button>
                      </>
                    )}
                  </div>
                )}
                {imageSources.length > 1 && (
                  <div className="gallery-thumbnails">
                    {imageSources.map((img, index) => (
                      <img key={index} src={img} alt={`${product.name} thumbnail ${index + 1}`} className={index === currentImageIndex ? 'active' : ''} onClick={() => setCurrentImageIndex(index)} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              imageSrc && (
                <div style={{ width: '100px', height: '100px', borderRadius: '16px', alignSelf: 'center', overflow: 'hidden', flexShrink: 0, backgroundColor: '#f4f4f4' }}>
                  <img src={imageSrc} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
              )
            )}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', textAlign: isChefSpecialty ? 'center' : 'left' }}>
              <h2 style={{ fontSize: '26px', fontWeight: '800', margin: '0', color: '#111', lineHeight: '1.2', letterSpacing: '-0.02em' }}>{product.name}</h2>
              {!isChefSpecialty && product.brand && <div style={{ fontSize: '14px', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{product.brand}</div>}
              {!isChefSpecialty && product.price && <span style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginTop: '4px' }}>{product.price}</span>}
              {!isChefSpecialty && product.prepTime && <span style={{ alignSelf: isChefSpecialty ? 'center' : 'flex-start', background: '#fff', border: '1px solid #eaeaea', padding: '6px 12px', borderRadius: '100px', fontSize: '13px', color: '#111', fontWeight: '600', marginTop: '4px' }}>⏱ {product.prepTime} mins</span>}
            </div>

            {product.description && (
              <p style={{ fontSize: '15px', color: '#555', margin: '0', lineHeight: '1.6', textAlign: isChefSpecialty ? 'center' : 'left' }}>
                {product.description}
              </p>
            )}

            {isChefSpecialty ? (
              <div className="product-cart-actions">
                <button className="vendors-save-btn" style={{ width: '100%' }} onClick={onStartChat}>Start Chat</button>
              </div>
            ) : (
              <div className="product-cart-actions">
                <div className="product-quantity-selector">
                  <button onClick={handleDecrease}>-</button>
                  <span>{quantity}</span>
                  <button onClick={handleIncrease} className="increment-btn">+</button>
                </div>
                <button 
                  className="vendors-save-btn product-add-cart-btn" 
                  style={{ opacity: product.inStock ? 1 : 0.5, cursor: product.inStock ? 'pointer' : 'not-allowed' }}
                  disabled={!product.inStock}
                  onClick={() => {
                    if (onAddToCart) {
                      onAddToCart({ ...product, quantity, selectedExtras, selectedSides, totalNumericPrice });
                    }
                    onClose();
                  }}
                >
                  <span>{product.inStock ? 'Add to Cart' : 'Out of Stock'}</span>
                  {product.inStock && product.price && <span>{displayPrice}</span>}
                </button>
              </div>
            )}
          </div>

          {/* Left Side: Extras and Tags */}
          {!isChefSpecialty && (
            <div className="product-modal-extras">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {product.weight && <span style={{ background: '#f4f4f4', padding: '8px 14px', borderRadius: '100px', fontSize: '13px', color: '#111', fontWeight: '600' }}>⚖️ {product.weight}</span>}
              {product.dietaryPreference && product.dietaryPreference !== "None" && <span style={{ background: '#e6f8eb', color: '#00b84c', padding: '8px 14px', borderRadius: '100px', fontSize: '13px', fontWeight: '700' }}>{product.dietaryPreference}</span>}
              {product.spiceLevel && product.spiceLevel !== "None" && <span style={{ background: '#fef0f0', color: '#ff3b30', padding: '8px 14px', borderRadius: '100px', fontSize: '13px', fontWeight: '700' }}>🌶 {product.spiceLevel}</span>}
            </div>

            {product.cutlery && product.cutlery.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '-0.01em' }}>
                  Cutlery
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#888', background: '#eaeaea', padding: '4px 10px', borderRadius: '100px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Optional</span>
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {product.cutlery.map((item, idx) => (
                    <label key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                      <span style={{ fontSize: '15px', color: '#111', fontWeight: '500' }}>{item}</span>
                      <input type="checkbox" className="circular-checkbox" />
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(product.extras && product.extras.length > 0) || (product.sides && product.sides.length > 0) ? (
              <>
                {product.extras && product.extras.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '-0.01em' }}>
                      Extras
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#888', background: '#eaeaea', padding: '4px 10px', borderRadius: '100px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Optional</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {product.extras.map(extra => (
                        <label key={extra.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '15px', color: '#111', fontWeight: '500' }}>{extra.name}</span>
                            <span style={{ fontSize: '14px', color: '#666', fontWeight: '400' }}>+ ₦ {extra.price.toLocaleString()}</span>
                          </div>
                          <input
                            type="checkbox"
                            className="circular-checkbox"
                            checked={selectedExtras.some(e => e.id === extra.id)}
                            onChange={() => handleExtraToggle(extra)}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {product.sides && product.sides.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111', margin: '20px 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '-0.01em' }}>
                      Sides
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#888', background: '#eaeaea', padding: '4px 10px', borderRadius: '100px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Optional</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {product.sides.map(side => (
                        <label key={side.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '15px', color: '#111', fontWeight: '500' }}>{side.name}</span>
                            <span style={{ fontSize: '14px', color: '#666', fontWeight: '400' }}>+ ₦ {side.price.toLocaleString()}</span>
                          </div>
                          <input
                            type="checkbox"
                            className="circular-checkbox"
                            checked={selectedSides.some(s => s.id === side.id)}
                            onChange={() => handleSideToggle(side)}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                <p style={{ color: '#888', fontSize: '16px', margin: 0, textAlign: 'center', fontWeight: '500' }}>No extras or sides available for this item.</p>
              </div>
            )}
            </div>
          )}
          </div>

        </div>
      </div>
  );
}

export default ProductDetailsModal;